import { TypedDocumentNode, gql } from '@apollo/client';
import { CSSProperties, useState } from 'react';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { RewardType } from '@sorare/core/src/lib/rewards';

import { LazyBoxOverviewDialog } from '@sorare/gameplay/src/components/rewards/LazyBoxOverviewDialog';
import { RewardOverview } from '@sorare/gameplay/src/components/rewards/RewardOverview';
import { Variant } from '@sorare/gameplay/src/components/rewards/RewardOverview/types';

import { ObjectiveDescription } from 'components/compose/ObjectiveBar/ObjectiveDescription';

import type {
  BaseObjectiveBar_probabilisticBundleConfig,
  BaseObjectiveBar_so5Leaderboard,
} from './__generated__/index.graphql';

const Root = styled(Horizontal).attrs({ center: true })`
  padding: var(--unit);
  background: linear-gradient(180deg, var(--color) 0%, transparent 100%);
`;

type Props = {
  so5Leaderboard: BaseObjectiveBar_so5Leaderboard;
  probabilisticBundleConfig?: BaseObjectiveBar_probabilisticBundleConfig;
  description: string;
  rewardCount: number;
  completedOrClaimed: boolean;
};
export const BaseObjectiveBar = ({
  so5Leaderboard,
  probabilisticBundleConfig,
  rewardCount,
  description,
  completedOrClaimed,
}: Props) => {
  const [oddsDialogOpen, setOddsDialogOpen] = useState(false);

  return (
    <Root
      style={
        { '--color': so5Leaderboard.so5LeaderboardGroup.color } as CSSProperties
      }
    >
      <ObjectiveDescription
        description={description}
        completedOrClaimed={completedOrClaimed}
      />
      <ButtonBase
        onClick={() => {
          setOddsDialogOpen(true);
        }}
      >
        <RewardOverview
          variant={Variant.INLINE}
          simpleReward={{
            type: RewardType.PROBABILISTIC_BUNDLE,
            count: rewardCount,
            rarity: Rarity.limited,
          }}
        />
      </ButtonBase>
      {probabilisticBundleConfig && (
        <LazyBoxOverviewDialog
          open={oddsDialogOpen}
          onClose={() => setOddsDialogOpen(false)}
          boxConfigId={probabilisticBundleConfig.id}
        />
      )}
    </Root>
  );
};

BaseObjectiveBar.fragments = {
  so5Leaderboard: gql`
    fragment BaseObjectiveBar_so5Leaderboard on So5Leaderboard {
      slug
      so5LeaderboardGroup {
        slug
        color
      }
    }
  ` as TypedDocumentNode<BaseObjectiveBar_so5Leaderboard>,
  probabilisticBundleConfig: gql`
    fragment BaseObjectiveBar_probabilisticBundleConfig on ProbabilisticBundleConfig {
      id
    }
  ` as TypedDocumentNode<BaseObjectiveBar_probabilisticBundleConfig>,
};
